<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Equipment Specifications Look up"
      description="Enter as much information as you can to search our database. For example if you enter (cisco 4 port) you will get a listing of all products with Cisco 4 port in their product description. Then click on view to see the details."
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <div
          class="mb-6 flex items-center justify-between gap-3 sm:flex-col sm:gap-4"
        >
          <div class="flex-1 sm:w-full">
            <div class="relative flex items-center">
              <Search
                :size="20"
                :stroke-width="1.75"
                class="absolute left-4 opacity-40"
              />
              <AppInput
                :mode="mode"
                type="search"
                placeholder="Search powerspecs"
                v-model="searchQuery"
                classes="!pl-11"
                :hasLabel="false"
              />
            </div>
          </div>
          <div class="w-[200px] sm:w-full">
            <AppButton title="Search" @action="search" />
          </div>
        </div>
        <template v-if="paginatedItems.length > 0">
          <AppTable :mode="mode" :columns="columns">
            <template #rows>
              <tr v-for="(powerspec, key) in paginatedItems" :key="key">
                <td>
                  <FallbackText
                    :value="powerspec._source.equipment.equipment_id"
                    :isTruncate="false"
                  />
                </td>
                <td>
                  <FallbackText
                    :value="powerspec._source.equipment.prod_description"
                    :isTruncate="false"
                  />
                </td>
                <td>
                  <FallbackText
                    :value="powerspec._source.equipment.manufacturer_name"
                    :isTruncate="false"
                  />
                </td>
                <td>
                  <FallbackText
                    :value="powerspec._source.equipment.manufacturer_prod_id"
                    :isTruncate="false"
                  />
                </td>
                <td>
                  <div class="flex items-center justify-center">
                    <TableAction
                      :mode="mode"
                      @itemAction="viewPowerSpec(powerspec._source.equipment)"
                      action="view"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              id: 'ID',
              description: 'Description',
              manufacturer: 'Manufacturer',
              productId: 'Mfr Product ID',
              actions: 'Actions',
            }"
            :data="paginatedItems"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'id'">
                <FallbackText
                  :value="row._source.equipment.equipment_id"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'description'">
                <FallbackText
                  :value="row._source.equipment.prod_description"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'manufacturer'">
                <FallbackText
                  :value="row._source.equipment.manufacturer_name"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'productId'">
                <FallbackText
                  :value="row._source.equipment.manufacturer_prod_id"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="viewPowerSpec(row._source.equipment)"
                    action="view"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData
          v-else
          :notFound="'No powerspecs represented!'"
          @clear="clear"
          :isClear="searchQuery ? true : false"
          :mode="mode"
        />
        <AppPagination
          :mode="mode"
          :data="powerspecs"
          @pageChanged="updatePaginatedItems"
        />
      </template>
    </AppCard>
    <EquipSpecSheet
      v-if="currentPowerSpec"
      :powerspec="currentPowerSpec"
      :mode="mode"
      @close="close"
    />
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import EquipSpecSheet from "@C/EquipSpecSheet.vue";
import { Search } from "lucide-vue-next";
import EmptyData from "@C/Common/EmptyData.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import AppPagination from "@C/Common/AppPagination.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import AppButton from "@C/Common/AppButton.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "EquipSpecSheets",
  components: {
    AuthLayout,
    Section,
    EquipSpecSheet,
    Search,
    EmptyData,
    AppPagination,
    AppTable,
    AppCard,
    TableAction,
    AppInput,
    FallbackText,
    MobileTable,
    AppButton,
  },
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  data() {
    return {
      powerspecs: [],
      searchQuery: "",
      currentPowerSpec: null,
      paginatedItems: [],
      columns: [
        {
          name: "ID",
        },
        {
          name: "Description",
        },
        {
          name: "Manufacturer",
        },
        {
          name: "Mfr Product ID",
        },
        {
          name: "Actions",
        },
      ],
    };
  },
  methods: {
    async search() {
      this.loaderStore.setIsLoading(true);
      const payload = { search_string: this.searchQuery };
      try {
        const { data } = await ApiRequest().post(`/equipment_search`, payload);
        if (data) {
          this.powerspecs = data;
        } else {
          this.powerspecs = [];
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    clear() {
      this.searchQuery = "";
    },
    viewPowerSpec(powerspec) {
      this.currentPowerSpec = powerspec;
    },
    close() {
      this.currentPowerSpec = null;
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
  },
};
</script>
